import { useContext } from 'react';
import { SubmissionContext } from '../Context/SubmissionProvider';
import { Tooltip } from '@mui/material';
import { DOCS_URL } from '../../../../SideMenu/Menu';
import { StyledBodyText } from '../../../../../../common/Styleguide/Common/Text';
import { StyledInput } from '../../../../../../common/Styleguide/Common/Input';
import { MAX_LOBBY_SIZE, MIN_LOBBY_SIZE } from './baseFormHelper';
import TextAlert from '../../TextAlert';
import { COLORS } from '../../../../../../common/Styleguide/Common/colors';
import { Help } from '@mui/icons-material';

const MultiplayerLobbyEditor: React.FC = () => {
  const { baseForm, baseFormProblems, updateBaseForm, disableDetailsEditing } = useContext(SubmissionContext);

  if (baseForm.gameLoaderType === null || !baseForm?.multiplayerOptions?.isMultiplayer) {
    return null;
  }

  const renderMultiplayerOptions = () => {
    const onMinLobbySizeChange = (minLobbySizeValue: string) => {
      const minLobbySize = parseInt(minLobbySizeValue);
      updateBaseForm({
        ...baseForm,
        multiplayerOptions: {
          ...baseForm.multiplayerOptions,
          minLobbySize: isNaN(minLobbySize) ? undefined : minLobbySize,
        },
      });
    };

    const onMaxLobbySizeChange = (maxLobbySizeValue: string) => {
      const maxLobbySize = parseInt(maxLobbySizeValue);
      updateBaseForm({
        ...baseForm,
        multiplayerOptions: {
          ...baseForm.multiplayerOptions,
          maxLobbySize: isNaN(maxLobbySize) ? undefined : maxLobbySize,
        },
      });
    };

    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <StyledBodyText variant="h3">Multiplayer lobby size:</StyledBodyText>
          Min
          <StyledInput
            width={64}
            height={32}
            disabled={disableDetailsEditing}
            value={baseForm.multiplayerOptions?.minLobbySize ?? ''}
            onChange={(e) => onMinLobbySizeChange(e.currentTarget.value)}
          />
          Max
          <StyledInput
            width={64}
            height={32}
            disabled={disableDetailsEditing}
            value={baseForm.multiplayerOptions?.maxLobbySize ?? ''}
            onChange={(e) => onMaxLobbySizeChange(e.currentTarget.value)}
          />
          <Tooltip title={'Lobby size is displayed on our multiplayer page.'} arrow>
            <Help sx={{ width: 20 }} />
          </Tooltip>
        </div>
        {baseFormProblems?.includes('MULTIPLAYER_INVALID_LOBBY') && (
          <StyledBodyText color="alert60" sx={{ marginBlock: 0 }}>
            Please fill both lobby size fields correctly
          </StyledBodyText>
        )}
        {baseFormProblems?.includes('MULTIPLAYER_INVALID_LOBBY_MIN') && (
          <StyledBodyText color="alert60" sx={{ marginBlock: 0 }}>
            Minimum lobby size must be at least {MIN_LOBBY_SIZE}
          </StyledBodyText>
        )}
        {baseFormProblems?.includes('MULTIPLAYER_INVALID_LOBBY_MAX') && (
          <StyledBodyText color="alert60" sx={{ marginBlock: 0 }}>
            Maximum lobby size must be lower than {MAX_LOBBY_SIZE}
          </StyledBodyText>
        )}
        <TextAlert>
          We have specific requirements for multiplayer games, please refer to our{' '}
          <a
            href={`${DOCS_URL}/requirements/multiplayer/`}
            target="_blank"
            rel="noreferrer"
            style={{ color: COLORS.warning[100], textDecoration: 'underline' }}
          >
            documentation
          </a>{' '}
          . Games will not be accepted if they don't comply to these requirements:
          <ul>
            <li>The game should use invite link & button functionality</li>
            <li>
              The game supports{' '}
              <a
                href={`${DOCS_URL}/sdk/game/#instant-multiplayer`}
                style={{ color: COLORS.warning[100], textDecoration: 'underline' }}
                target="blank"
              >
                IsInstantMultiplayer
              </a>{' '}
              so party leaders can create/join a lobby directly
            </li>
          </ul>
        </TextAlert>
        {disableDetailsEditing && (
          <TextAlert>
            It is not possible at the moment to change the multiplayer options for released games. Please contact us at
            submissions@crazygames.com if you would like to change them.
          </TextAlert>
        )}
      </>
    );
  };

  return <>{renderMultiplayerOptions()}</>;
};

export default MultiplayerLobbyEditor;
